import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserPlus, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { value: 'ADMIN', label: 'Amministratore' },
  { value: 'ACCOUNTANT', label: 'Commercialista' },
  { value: 'LABOR_CONSULTANT', label: 'Consulente del Lavoro' },
  { value: 'RESIDENT', label: 'Condomino' },
];

export const UserManagement = ({ condominiumId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('RESIDENT');

  useEffect(() => {
    fetchUsers();
  }, [condominiumId]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/condominiums/${condominiumId}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli utenti');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/condominiums/${condominiumId}/users`, {
        user_email: userEmail,
        role: selectedRole,
      });
      toast.success('Utente aggiunto con successo!');
      setOpenAddDialog(false);
      setUserEmail('');
      setSelectedRole('RESIDENT');
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiunta');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!confirm('Sei sicuro di voler rimuovere questo utente?')) return;

    try {
      await axios.delete(`${API}/condominiums/${condominiumId}/users/${userId}`);
      toast.success('Utente rimosso');
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la rimozione');
    }
  };

  const getRoleLabel = (role) => {
    return ROLES.find((r) => r.value === role)?.label || role;
  };

  return (
    <Card className="border-stone-200 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl font-heading font-semibold text-slate-900">
          Gestione Utenti
        </CardTitle>
        <Dialog open={openAddDialog} onOpenChange={setOpenAddDialog}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="add-user-button"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Aggiungi Utente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aggiungi Utente al Condominio</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <Label htmlFor="userEmail">Email Utente *</Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="utente@email.it"
                  required
                  data-testid="user-email-input"
                />
                <p className="text-xs text-stone-600 mt-1">
                  L'utente deve essere già registrato sulla piattaforma
                </p>
              </div>
              <div>
                <Label htmlFor="role">Ruolo *</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="submit-add-user"
              >
                {loading ? 'Aggiunta...' : 'Aggiungi Utente'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <p className="text-sm text-stone-600 text-center py-8">
            Nessun utente nel condominio
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <TableCell className="font-medium">{user.full_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-stone-400" />
                      {getRoleLabel(user.role)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveUser(user.user_id)}
                      className="text-red-700 hover:text-red-900 hover:bg-red-50"
                      data-testid={`remove-user-${user.user_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
